import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { servicesApi, type CatalogService } from '../../api/services'
import { categoriesApi, type Category } from '../../api/categories'
import { Modal } from '../../components/ui/Modal'

interface SvcForm { id?: string; name: string; nameIt: string; price: string; isActive: boolean }
const EMPTY_SVC: SvcForm = { name: '', nameIt: '', price: '', isActive: true }
interface CatForm { id?: string; name: string; nameIt: string; isActive: boolean }
const EMPTY_CAT: CatForm = { name: '', nameIt: '', isActive: true }

const dispName = (en: string | null, it: string | null) => en || it || '—'

export function ServicesSettings() {
  const { companyId = '' } = useParams()
  const qc = useQueryClient()

  // ── Services ──
  const [svcForm, setSvcForm] = useState<SvcForm | null>(null)
  const [search, setSearch] = useState('')
  const { data: services = [], isLoading: svcLoading } = useQuery({
    queryKey: ['services', companyId, { includeInactive: true, search }],
    queryFn: () => servicesApi.list(companyId, { includeInactive: true, search }),
  })
  const invalidateSvc = () => qc.invalidateQueries({ queryKey: ['services', companyId] })
  const saveSvc = useMutation({
    mutationFn: (f: SvcForm) =>
      f.id
        ? servicesApi.update(companyId, f.id, { name: f.name, nameIt: f.nameIt, price: Number(f.price), isActive: f.isActive })
        : servicesApi.create(companyId, { name: f.name || undefined, nameIt: f.nameIt || undefined, price: Number(f.price) }),
    onSuccess: () => { invalidateSvc(); setSvcForm(null) },
  })
  const delSvc = useMutation({ mutationFn: (id: string) => servicesApi.remove(companyId, id), onSuccess: invalidateSvc })

  // ── Categories ──
  const [catForm, setCatForm] = useState<CatForm | null>(null)
  const { data: categories = [], isLoading: catLoading } = useQuery({
    queryKey: ['categories', companyId, { includeInactive: true }],
    queryFn: () => categoriesApi.list(companyId, { includeInactive: true }),
  })
  const invalidateCat = () => qc.invalidateQueries({ queryKey: ['categories', companyId] })
  const saveCat = useMutation({
    mutationFn: (f: CatForm) =>
      f.id
        ? categoriesApi.update(companyId, f.id, { name: f.name, nameIt: f.nameIt, isActive: f.isActive })
        : categoriesApi.create(companyId, { name: f.name || undefined, nameIt: f.nameIt || undefined }),
    onSuccess: () => { invalidateCat(); setCatForm(null) },
  })
  const delCat = useMutation({ mutationFn: (id: string) => categoriesApi.remove(companyId, id), onSuccess: invalidateCat })

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* SERVICES */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Services</h2>
            <p className="text-sm text-gray-500">Price list used when building proformas. Deleting keeps snapshots on existing proformas.</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setSvcForm(EMPTY_SVC)}><Plus className="w-4 h-4" /> Add</button>
        </div>
        <input className="input mb-3" placeholder="Search services…" value={search} onChange={e => setSearch(e.target.value)} />
        {svcLoading ? <p className="text-sm text-gray-500">Loading…</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-2">Name</th><th className="py-2 text-right">Price</th><th className="py-2 text-center">Active</th><th className="py-2 w-20"></th>
            </tr></thead>
            <tbody>
              {services.map((s: CatalogService) => (
                <tr key={s.id} className="border-b last:border-0">
                  <td className="py-2">{dispName(s.name, s.nameIt)}</td>
                  <td className="py-2 text-right">{Number(s.price).toFixed(2)}</td>
                  <td className="py-2 text-center">{s.isActive ? '✓' : '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="btn btn-ghost btn-sm" onClick={() => setSvcForm({ id: s.id, name: s.name ?? '', nameIt: s.nameIt ?? '', price: String(Number(s.price)), isActive: s.isActive })}><Pencil className="w-4 h-4" /></button>
                    <button className="btn btn-ghost btn-sm text-red-600" onClick={() => { if (confirm(`Delete "${dispName(s.name, s.nameIt)}"?`)) delSvc.mutate(s.id) }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {!services.length && <tr><td colSpan={4} className="py-6 text-center text-gray-400">No services yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* CATEGORIES */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Categories</h2>
            <p className="text-sm text-gray-500">Group proforma lines (e.g. Upper Arch). Independent from services.</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setCatForm(EMPTY_CAT)}><Plus className="w-4 h-4" /> Add</button>
        </div>
        {catLoading ? <p className="text-sm text-gray-500">Loading…</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-2">Name</th><th className="py-2 text-center">Active</th><th className="py-2 w-20"></th>
            </tr></thead>
            <tbody>
              {categories.map((c: Category) => (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="py-2">{dispName(c.name, c.nameIt)}</td>
                  <td className="py-2 text-center">{c.isActive ? '✓' : '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="btn btn-ghost btn-sm" onClick={() => setCatForm({ id: c.id, name: c.name ?? '', nameIt: c.nameIt ?? '', isActive: c.isActive })}><Pencil className="w-4 h-4" /></button>
                    <button className="btn btn-ghost btn-sm text-red-600" onClick={() => { if (confirm(`Delete "${dispName(c.name, c.nameIt)}"?`)) delCat.mutate(c.id) }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {!categories.length && <tr><td colSpan={3} className="py-6 text-center text-gray-400">No categories yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* Service modal */}
      <Modal open={!!svcForm} onClose={() => setSvcForm(null)} title={svcForm?.id ? 'Edit service' : 'Add service'} size="sm">
        {svcForm && (
          <>
            <label className="label">Name (EN)</label>
            <input className="input mb-3" value={svcForm.name} onChange={e => setSvcForm({ ...svcForm, name: e.target.value })} />
            <label className="label">Nome (IT)</label>
            <input className="input mb-3" value={svcForm.nameIt} onChange={e => setSvcForm({ ...svcForm, nameIt: e.target.value })} />
            <label className="label">Price</label>
            <input className="input mb-3" type="number" min="0" step="0.01" value={svcForm.price} onChange={e => setSvcForm({ ...svcForm, price: e.target.value })} />
            {svcForm.id && (
              <label className="flex items-center gap-2 text-sm mb-3">
                <input type="checkbox" className="w-3.5 h-3.5" checked={svcForm.isActive} onChange={e => setSvcForm({ ...svcForm, isActive: e.target.checked })} /> Active
              </label>
            )}
            <div className="flex justify-end gap-2 mt-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setSvcForm(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm"
                disabled={(!svcForm.name.trim() && !svcForm.nameIt.trim()) || svcForm.price === '' || Number(svcForm.price) < 0 || saveSvc.isPending}
                onClick={() => saveSvc.mutate(svcForm)}>{svcForm.id ? 'Save' : 'Create'}</button>
            </div>
          </>
        )}
      </Modal>

      {/* Category modal */}
      <Modal open={!!catForm} onClose={() => setCatForm(null)} title={catForm?.id ? 'Edit category' : 'Add category'} size="sm">
        {catForm && (
          <>
            <label className="label">Name (EN)</label>
            <input className="input mb-3" value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} />
            <label className="label">Nome (IT)</label>
            <input className="input mb-3" value={catForm.nameIt} onChange={e => setCatForm({ ...catForm, nameIt: e.target.value })} />
            {catForm.id && (
              <label className="flex items-center gap-2 text-sm mb-3">
                <input type="checkbox" className="w-3.5 h-3.5" checked={catForm.isActive} onChange={e => setCatForm({ ...catForm, isActive: e.target.checked })} /> Active
              </label>
            )}
            <div className="flex justify-end gap-2 mt-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setCatForm(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm"
                disabled={(!catForm.name.trim() && !catForm.nameIt.trim()) || saveCat.isPending}
                onClick={() => saveCat.mutate(catForm)}>{catForm.id ? 'Save' : 'Create'}</button>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}
